/**
 * Privacy policy — required for a kids app (COPPA / GDPR-K).
 *
 * Written in plain language for parents. Bilingual (EN / NL) via the
 * family's locale. Content is hand-crafted — no LLM — so claims about
 * our actual data practices are not hallucinated.
 */

import { useLocation } from 'preact-iso';
import { currentUser } from '../state/user';

const EN = {
  title: 'Privacy',
  lastUpdated: 'Last updated: 2026-04-21',
  whatWeCollect: 'What we collect',
  whatWeCollectBody: [
    'A randomly generated device ID stored in your browser (not linked to any account).',
    'Your child\'s first name (or a nickname) and age.',
    'Learning progress: which letters and stages your child has practised, stars earned, and errors made. Errors are stored so our AI tutor can adapt quizzes.',
    'An optional parent PIN, stored as a one-way cryptographic hash.',
  ],
  whatWeDont: 'What we do NOT collect',
  whatWeDontBody: [
    'Real names, email addresses, phone numbers, or any government-issued identifiers.',
    'Voice recordings. When your child speaks into the microphone, the audio is sent to our speech-to-text service, transcribed, and then discarded in memory. It is never saved to our storage.',
    'Photographs or video.',
    'Location data. We do not track where you or your child are.',
    'Any data used for advertising or sold to third parties.',
  ],
  whoSees: 'Who sees the data',
  whoSeesBody:
    'All data is stored on Cloudflare infrastructure (UK / EU edge preferred). The AI services that power pronunciation evaluation and session summaries process the data inside Cloudflare\'s network. No third-party analytics or advertising trackers are loaded.',
  yourRights: 'Your rights',
  yourRightsBody:
    'You can delete your family at any time by clearing this site\'s data in your browser settings. All associated progress and any stored PIN hash are removed from our database within 24 hours. Email us to request a manual deletion: noureddine@cloudflare.com.',
  back: 'Back',
};

const NL = {
  title: 'Privacy',
  lastUpdated: 'Laatst bijgewerkt: 21-04-2026',
  whatWeCollect: 'Wat we verzamelen',
  whatWeCollectBody: [
    'Een willekeurig gegenereerd apparaat-ID dat in uw browser wordt bewaard (niet gekoppeld aan een account).',
    'De voornaam (of bijnaam) en leeftijd van uw kind.',
    'Leervoortgang: geoefende letters en stages, verdiende sterren en gemaakte fouten. Fouten worden bewaard zodat onze AI-tutor de quizzen kan aanpassen.',
    'Een optionele oudercode, opgeslagen als een eenrichtings-hash.',
  ],
  whatWeDont: 'Wat we NIET verzamelen',
  whatWeDontBody: [
    'Echte namen, e-mailadressen, telefoonnummers of officiële identificatienummers.',
    'Spraakopnames. Als uw kind in de microfoon spreekt, wordt de audio verzonden naar onze speech-to-text-service, getranscribeerd en vervolgens in het geheugen verwijderd. Het wordt nooit opgeslagen.',
    'Foto\'s of video\'s.',
    'Locatiegegevens. We volgen niet waar u of uw kind zich bevindt.',
    'Gegevens voor advertenties of verkoop aan derden.',
  ],
  whoSees: 'Wie ziet de gegevens',
  whoSeesBody:
    'Alle gegevens worden opgeslagen op de infrastructuur van Cloudflare (bij voorkeur UK / EU edge). De AI-diensten voor uitspraakbeoordeling en samenvattingen verwerken de gegevens binnen het netwerk van Cloudflare. Er worden geen analysediensten of advertentietrackers van derden geladen.',
  yourRights: 'Uw rechten',
  yourRightsBody:
    'U kunt uw gezin op elk moment verwijderen door de sitegegevens in uw browser te wissen. Alle bijbehorende voortgang en de opgeslagen PIN-hash worden binnen 24 uur uit onze database verwijderd. Mail ons voor een handmatige verwijdering: noureddine@cloudflare.com.',
  back: 'Terug',
};

export function Privacy() {
  const { route } = useLocation();
  const t = currentUser.value?.locale === 'nl' ? NL : EN;

  return (
    <div
      class="flex-1 px-6 py-8 max-w-2xl mx-auto w-full prose prose-sm"
      dir="ltr"
    >
      <button
        type="button"
        onClick={() => route('/')}
        class="text-sm text-ink/60 underline mb-4"
      >
        ← {t.back}
      </button>

      <h1 class="text-3xl font-display text-coral mb-1">{t.title}</h1>
      <p class="text-xs text-ink/50 mb-8">{t.lastUpdated}</p>

      <h2 class="text-xl font-display mt-6">{t.whatWeCollect}</h2>
      <ul class="list-disc pl-5 space-y-2 mt-2 text-sm">
        {t.whatWeCollectBody.map((line) => (
          <li>{line}</li>
        ))}
      </ul>

      <h2 class="text-xl font-display mt-8">{t.whatWeDont}</h2>
      <ul class="list-disc pl-5 space-y-2 mt-2 text-sm">
        {t.whatWeDontBody.map((line) => (
          <li>{line}</li>
        ))}
      </ul>

      <h2 class="text-xl font-display mt-8">{t.whoSees}</h2>
      <p class="text-sm mt-2 leading-relaxed">{t.whoSeesBody}</p>

      <h2 class="text-xl font-display mt-8">{t.yourRights}</h2>
      <p class="text-sm mt-2 leading-relaxed">{t.yourRightsBody}</p>
    </div>
  );
}
